




































































import { Component, Vue, Watch, Prop } from "vue-property-decorator";
import DoctorSelector from "@/components/inputs/DoctorSelector.vue";
import PatientSelector from "@/components/inputs/PatientSelector.vue";
import { PatientModel } from "@/models/patient";
import { EventModel, EventType, EventStatus } from "@/models/event";
import EventDetail from "@/components/EventDetail/index.vue";
import EventsTableCard from "@/components/EventsTableCard.vue";
import { EventTypeIcon, EventTypeSelectionCard } from "@/components/EventType";
import { auth as authStore, auth, patient as store } from "@/store/modules";
import { format, parse } from "date-fns";
import { inject } from "inversify-props";
import { IEventService, IPatientService, EventFilter } from "@/interfaces/Services";
import MassActionPerformModal, { MassActionItem } from '@/components/MassActionPerformModal.vue';
import { DateInput } from '@/components/inputs';
import DependencyIdentifiers from '@/ioc/DependencyIdentifiers';

export enum EventActionable { IsPending = 'IsPending' }

@Component({
  components: {
    DoctorSelector,
    PatientSelector,
    EventDetail,
    EventsTableCard,
    EventTypeIcon,
    EventTypeSelectionCard,
    MassActionPerformModal,
    DateInput
  }
})
export class Events extends Vue {
  @inject(DependencyIdentifiers.EventService)
  private eventService!: IEventService;
  @inject(DependencyIdentifiers.PatientService)
  private patientService!: IPatientService;

  private isLoading = false;
  private componentKey = 0;
  private searchQuery = "";
  private tableOptions: any = {};
  private filter: EventFilter = {
    sortBy: new Map(),
    patientId: '',
    doctorId: '',
    types: [],
    statuses: [],
    searchQuery: '',
    from: '',
    until: '',
  };
  
  private get statusMap() {
    return ["draft", "pending", "approved", "rejected"]
      .map((status) => ({
        value: status,
        text: this.$t(`event.status.${status}`) as string
      }));
  }

  private expanded: EventModel[] = [];
  private patients: Array<PatientModel> = [];
  private singleExpand = false;
  private searchQueryDebounceTimer: NodeJS.Timeout | null = null;

  get isPatient() {
    return authStore.isPatient;
  }

  async mounted() {
    this.loadUrlParameters();
    if (this.isPatient) {
      this.filter.patientId = authStore.authPatientId;
    }

    await this.loadPatients();
  }

  /**
   * TODO: Use autocomplete for patient search and joined patient data for event listing.
   */
  private async loadPatients() {
    try {
      const page = 1,
        itemsPerPage = 100;
      const [patients, {}] = await this.patientService.getMany(
        page,
        itemsPerPage
      );
      this.patients = [...patients];
    } finally {
      this.isLoading = false;
    }
  }

  resolvePatientById(patientId: string) {
    let patient = Object.values(store.items).find(
      ({ $patientId }) => $patientId === patientId
    );
    if (patient) {
      return patient.name;
    }
    patient = this.patients.find(({ $id }) => $id === patientId);
    if (patient) {
      return patient.name;
    }
    return null;
  }

  resolveStatus(raw: string): string {
    const match = this.statusMap.find(i => i.value == raw);
    return match ? match.text : raw;
  }

  private isSelectedActionable(
    items: EventModel[],
    check: EventActionable,
    matchAll: boolean = true,
  ): boolean {
    let matchCount: number = 0;
    items.forEach((item) => {
      switch (check) {
        case EventActionable.IsPending:
          if (item.status == EventStatus.Pending) {
            matchCount++;
          }
          break;
        default:
          throw new Error(`Unknown [EventActionable.${check}].`);
          break;
      }
    });

    return matchAll ? matchCount == items.length : matchCount > 0;
  }

  loadUrlParameters() {
    const query = this.$route.query;

    this.filter.types = query.types ? (query.types as string).split(",") as EventType[]: [];
    this.filter.statuses = query.statuses ? (query.statuses as string).split(",") as EventStatus[] : [];
    this.filter.from = query.dateFrom ? query.dateFrom.toString() : null;
    this.filter.until = query.until ? query.until.toString() : null;
    this.filter.patientId = query.patientId ? query.patientId.toString() : null;
    this.filter.doctorId = query.doctorId ? query.doctorId.toString() : null;
    this.filter.searchQuery = query.searchQuery ? query.searchQuery.toString() : '';
    this.tableOptions.page = query.page ? parseInt(query.page.toString()) : 1;
    this.tableOptions.itemsPerPage = query.itemsPerPage
      ? parseInt(query.itemsPerPage.toString())
      : this.tableOptions.itemsPerPage;
    this.searchQuery = this.filter.searchQuery;
    this.componentKey++;
  }

  saveUrlParameters() {
    const query = {
      types: this.filter.types ? this.filter.types.join(',') : '',
      statuses: this.filter.statuses ? this.filter.statuses.join(',') : '',
      from: this.filter.from ? this.filter.from.toString() : '',
      until: this.filter.until ? this.filter.until.toString() : '',
      patientId: this.filter.patientId,
      doctorId: this.filter.doctorId,
      searchQuery: this.filter.searchQuery,
      page: `${this.tableOptions.page}`,
      itemsPerPage: `${this.tableOptions.itemsPerPage}`
    };

    const serial = JSON.stringify(query);
    const current = JSON.stringify(this.$router.currentRoute.query);
    if (serial == current) { return; }
    this.$router.push({path: '/dashboard/events', query: query as any });
  }

  async onFilterPatientChange(patientId: string | null) {
    this.filter.patientId = patientId;
  }

  async onFilterDoctorChange(doctorId: string | null) {
    this.filter.doctorId = doctorId;
  }

  @Watch("searchQuery")
  async onSearchQueryChange(val: string, oldVal: string) {
    if (val == null || val == oldVal) {
      return;
    }

    if (this.searchQueryDebounceTimer != null) {
      clearTimeout(this.searchQueryDebounceTimer);
    }
    this.searchQueryDebounceTimer = setTimeout(
      async () => this.filter.searchQuery = this.searchQuery,
      200
    );
  }

  @Watch("filter", {deep: true})
  onFilterUpdate() {
    this.saveUrlParameters();
  }

  async onEventTypeSelectionChange(value: EventType[]) {
    this.filter.types = value.length === 0 ? null : value;
  }

  onStatusFilterChange(value: string[]) {
    this.filter.statuses = value as EventStatus[];
  }

  onFilterFromChanged(date: string) {
    this.filter.from = date;
  }

  onFilterUntilChanged(date: string) {
    this.filter.until = date;
  }

  onTableOptionsChanged(options: any) {
    this.tableOptions = options;
    this.saveUrlParameters();
  }
}

export default Events;
